import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/FirebaseAuthContext';
import Logo from '../../components/ui/Logo';

const genres = [
  { id: 'fiction', name: 'Fiction', icon: '📚' },
  { id: 'non-fiction', name: 'Non-Fiction', icon: '🧠' },
  { id: 'mystery', name: 'Mystery & Thriller', icon: '🔍' },
  { id: 'science-fiction', name: 'Science Fiction', icon: '🚀' },
  { id: 'history', name: 'History', icon: '🏛️' },
  { id: 'romance', name: 'Romance', icon: '💝' },
  { id: 'biography', name: 'Biography', icon: '👤' },
  { id: 'fantasy', name: 'Fantasy', icon: '🐉' },
  { id: 'self-help', name: 'Self-Help', icon: '🌱' },
  { id: 'business', name: 'Business & Finance', icon: '💼' },
  { id: 'psychology', name: 'Psychology', icon: '🌀' },
  { id: 'horror', name: 'Horror', icon: '👻' },
  { id: 'politics', name: 'Politics & Society', icon: '🌍' },
  { id: 'health', name: 'Health & Wellness', icon: '🧘' },
  { id: 'poetry', name: 'Poetry', icon: '📝' }
];

const GenreSelection: React.FC = () => {
  const navigate = useNavigate();
  const { updateUserPreferences, user } = useAuth();
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const toggleGenre = (genreId: string) => {
    setSelectedGenres(prev => {
      if (prev.includes(genreId)) {
        return prev.filter(id => id !== genreId);
      }
      return [...prev, genreId];
    });
    setError('');
  };

  const handleContinue = async () => {
    if (selectedGenres.length === 0) {
      setError('Please select at least one genre');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      if (!user) {
        setError('You must be logged in to continue. Please log in again.');
        setIsLoading(false);
        return;
      }
      await updateUserPreferences({
        genres: selectedGenres,
        hasCompletedOnboarding: false,
        goals: {
          booksPerYear: 12,
          readingTimePerDay: 30,
          readingDaysPerWeek: 5
        }
      });
      navigate('/onboarding/goals');
    } catch (error) {
        setError('Failed to save preferences. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#fcfaf3]">
      {/* Left: Progress/Welcome */}
      <div className="hidden md:flex flex-col justify-between w-full max-w-[400px] bg-[#2d2d2d] text-white py-10 px-8 min-h-screen">
        <div>
          <div className="flex items-center mb-2">
            <Logo variant="light" />
          </div>
          <div className="mt-12">
            <h2 className="text-2xl font-semibold mb-2">Welcome to Smata</h2>
            <p className="text-base text-gray-300 mb-10">Let's set up your reading experience in just a few steps</p>
            <div className="flex flex-col items-start gap-0 mt-8">
              <div className="flex items-center gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-white bg-white text-[#2d2d2d] font-bold">1</div>
                  <div className="border-l-2 border-dotted border-gray-400" style={{ height: '36px' }}></div>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-gray-400 text-gray-400 font-bold opacity-60">2</div>
                </div>
                <div className="flex flex-col justify-between h-[88px] py-1">
                  <span className="text-base">Tell us about your reading preferences</span>
                  <span className="text-base opacity-60">Set your reading goals</span>
                </div>
              </div>
            </div>
      </div>
        </div>
        <div></div>
      </div>
      {/* Right: Genre Selection */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 sm:px-8 py-12">
        <div className="w-full max-w-2xl">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">What do you like to read?</h1>
          <p className="mb-8 text-gray-600">Select your favorite genres to get personalized recommendations</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          {genres.map(genre => (
            <button
              key={genre.id}
              onClick={() => toggleGenre(genre.id)}
                className={`flex items-center w-full p-4 rounded-lg border transition-colors font-medium text-base focus:outline-none focus:ring-2 focus:ring-orange-400 focus:z-10
                  ${selectedGenres.includes(genre.id)
                    ? 'bg-[#ff6633] text-white border-[#ff6633] shadow-md'
                    : 'bg-white text-gray-900 border-gray-200 hover:border-orange-300'}
                `}
              disabled={isLoading}
                style={{ minHeight: 56 }}
            >
                <span className={`text-2xl mr-3 ${selectedGenres.includes(genre.id) ? 'text-white' : 'text-gray-700'}`}>{genre.icon}</span>
                <span>{genre.name}</span>
            </button>
          ))}
        </div>
        {error && (
            <div className="mb-4 text-red-600 text-sm">{error}</div>
        )}
          <button
            onClick={handleContinue}
            disabled={selectedGenres.length === 0 || isLoading}
            className="w-full py-3 rounded-full bg-[#2d2d2d] text-white font-semibold text-lg shadow-sm hover:bg-[#222] transition disabled:opacity-50 disabled:cursor-not-allowed mb-2"
          >
            {isLoading ? 'Loading...' : 'Continue'}
          </button>
          <p className="text-center text-gray-500 text-sm">
            {selectedGenres.length === 0 ? 'Please select at least one genre' : `${selectedGenres.length} genre${selectedGenres.length > 1 ? 's' : ''} selected`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default GenreSelection; 